import { useEffect, useState } from "react"
import {
  MY_VACATION_EDIT_APPROVED_FORM_INITIAL_DATA_STATE,
  MY_VACATION_EDIT_APPROVED_FORM_INITIAL_VALIDATION_STATE,
  MY_VACATION_REQUEST_FORM_INITIAL_AVAILABLE_DAYS_LIST,
  MY_VACATION_REQUEST_FORM_INITIAL_DATA_STATE,
  MY_VACATION_REQUEST_FORM_INITIAL_HOLIDAY_LIST,
  MY_VACATION_REQUEST_FORM_INITIAL_VALIDATION_STATE,
} from "../consts/modules/vacations.consts"
import { DateTime } from "luxon"
import _ from "lodash"
import {
  getClosestFutureBusinessDay,
  getNumberOfTypeDaysToUse,
  getTeamMembersEndOfPeriodMonth,
  getTeamMembersStartOfPeriodMonth,
  getValidWorkAnniversaryForDayOffValidity,
  isMexicanSchema,
} from "../utils/vacation.utils"
import {
  getTeamMemberBucketDaysAsOfToday,
  getTeamMemberBucketDaysResume,
  getTeamMemberBucketDaysResumeExcludeVacationID,
  getTeamMemberDisabledDaysInRange,
  getTeamMemberDisabledDaysInRangeExcludeVacationID,
  getTeamMemberPlusDaysResume,
  getTeamMemberPlusDaysResumeExcludeVacationID,
  getTeamMemberVacationResume,
  getTeamMemberVacationResumeExcludeVacationID,
} from "../services/vacation.service"
import useToast from "./useToast.hook"
import { isObjectEmpty } from "../utils/object.utils"
import { getAntiquityFromDate } from "../utils/date.utils"
import { useSession } from "./auth/useSession.hook"

/**
 * @typedef {object} DisabledDayListItem
 * @property {string} date
 */

export const VACATION_REQUEST_FORM_MODE = {
  CREATE: "create",
  EDIT: "edit_pending",
  EDIT_APPROVED: "edit_approved",
}
const useMyVacationRequestFormState = ({
  holidaySchemaId,
  vacationRequestFormMode = VACATION_REQUEST_FORM_MODE.CREATE,
}) => {
  // States
  const initialFormData = _.cloneDeep(
    vacationRequestFormMode === VACATION_REQUEST_FORM_MODE.EDIT_APPROVED ?
      MY_VACATION_EDIT_APPROVED_FORM_INITIAL_DATA_STATE
    : MY_VACATION_REQUEST_FORM_INITIAL_DATA_STATE
  )
  const initialValidationState = _.cloneDeep(
    vacationRequestFormMode === VACATION_REQUEST_FORM_MODE.EDIT_APPROVED ?
      MY_VACATION_EDIT_APPROVED_FORM_INITIAL_VALIDATION_STATE
    : MY_VACATION_REQUEST_FORM_INITIAL_VALIDATION_STATE
  )
  const [formData, setFormData] = useState(_.cloneDeep(initialFormData))
  const [validations, setValidations] = useState(
    _.cloneDeep(initialValidationState)
  )
  const [availableDaysList, setAvailableDaysList] = useState(
    _.cloneDeep(MY_VACATION_REQUEST_FORM_INITIAL_AVAILABLE_DAYS_LIST)
  )
  const [disabledDayList, setDisabledDayList] = useState(
    /** @type {DisabledDayListItem[]} */ (
      _.cloneDeep(MY_VACATION_REQUEST_FORM_INITIAL_HOLIDAY_LIST)
    )
  )

  const [bucketDaysList, setBucketDaysList] = useState(
    /** @type {import("../services/vacation.service.types").TeamMemberBucketDaysResumeExcludeVacationIDResponse | null} */
    (null)
  )
  const [plusDaysList, setPlusDaysList] = useState(null)
  const [accordedDaysList, setAccordedDaysList] = useState(null)
  const [multipleDays, setMultipleDays] = useState(false)
  const [daysToUse, setDaysToUse] = useState(1)
  const [typesOfDaysToUse, setTypesOfDaysToUse] = useState(0)
  const [disabledSaveButton, setDisabledSaveButton] = useState(true)
  const [allowFormStateChanges, setAllowFormStateChanges] = useState(false)
  const [loading, setLoading] = useState(false)
  const { setErrorMessage } = useToast()
  const { user } = useSession()
  const anniversaryDate =
    user?.workAnniversary ? DateTime.fromISO(user.workAnniversary) : null

  // Effects
  useEffect(() => {
    if (!allowFormStateChanges) {
      setDisabledSaveButton(true)
      return
    }
    if (
      daysToUse >
        formData.bucketDays +
          formData.plusDays +
          formData.accordedDays +
          formData.additionalDays ||
      formData.motive === "" ||
      (Object.keys(formData).includes("changeMotive") &&
        formData.changeMotive === "")
    ) {
      setDisabledSaveButton(true)
    } else {
      setDisabledSaveButton(false)
    }
  }, [formData, allowFormStateChanges])

  useEffect(() => {
    if (!allowFormStateChanges) {
      return
    }
    if (typesOfDaysToUse > 1) {
      resetCounters()
    } else {
      if (holidaySchemaId && !isMexicanSchema(holidaySchemaId)) {
        updateFormWithSingleTypeOfDayOff(
          0,
          0,
          availableDaysList.accordedDays,
          0
        )
        return
      }
      updateFormWithSingleTypeOfDayOff(
        holidaySchemaId && isMexicanSchema(holidaySchemaId) ?
          availableDaysList.bucketDays + 3
        : 0,
        availableDaysList.plusDays,
        availableDaysList.accordedDays,
        availableDaysList.additionalDays
      )
    }
  }, [daysToUse, allowFormStateChanges])

  useEffect(() => {
    let newStartDate = formData.requestDates[0]
    if (vacationRequestFormMode === VACATION_REQUEST_FORM_MODE.CREATE) {
      newStartDate = getClosestFutureBusinessDay(newStartDate, disabledDayList)
      setFormData((prev) => ({
        ...prev,
        requestDates: [newStartDate, newStartDate],
      }))
    }
  }, [disabledDayList, vacationRequestFormMode])

  // Functions
  const updateFormWithSingleTypeOfDayOff = async (
    bucketDays,
    plusDays,
    accordedDays,
    additionalDays
  ) => {
    if (bucketDays && bucketDays > 0) {
      setFormData({
        ...formData,
        bucketDays: daysToUse,
        plusDays: 0,
        accordedDays: 0,
        additionalDays: 0,
      })
    } else if (plusDays && plusDays > 0) {
      setFormData({
        ...formData,
        bucketDays: 0,
        plusDays: daysToUse,
        accordedDays: 0,
        additionalDays: 0,
      })
    } else if (accordedDays && accordedDays > 0) {
      setFormData({
        ...formData,
        bucketDays: 0,
        plusDays: 0,
        accordedDays: daysToUse,
        additionalDays: 0,
      })
    } else if (additionalDays && additionalDays > 0) {
      setFormData({
        ...formData,
        bucketDays: 0,
        plusDays: 0,
        accordedDays: 0,
        additionalDays: daysToUse,
      })
    }
  }

  const updateDaysToUse = (startPeriod, endPeriod, disabledDayListToUse) => {
    let disabledDate = null
    let isDisabled = false
    let countOfValidDates = 0

    if (startPeriod.hasSame(endPeriod, "day")) {
      setDaysToUse(1)
      return
    }

    let index = startPeriod
    do {
      isDisabled = false
      const isWeekend = index.weekday === 6 || index.weekday === 7

      if (disabledDayListToUse.length === 0) isDisabled = false
      else {
        isDisabled = disabledDayListToUse.some((disabledDay) => {
          disabledDate = DateTime.fromSQL(disabledDay.date)
          return disabledDate.hasSame(index, "day")
        })
      }

      if (!isWeekend && !isDisabled) {
        countOfValidDates += 1
      }

      index = index.plus({ days: 1 })
    } while (index <= endPeriod)

    setDaysToUse(countOfValidDates)
  }

  const resetCounters = async () => {
    setFormData({
      ...formData,
      bucketDays: 0,
      plusDays: 0,
      accordedDays: 0,
      additionalDays: 0,
    })
  }

  const resetForms = async () => {
    setFormData(_.cloneDeep(initialFormData))
    setValidations(_.cloneDeep(initialValidationState))
    setMultipleDays(false)
    setDaysToUse(1)
    setAllowFormStateChanges(false)
  }

  // Services
  const updateTeamMemberBucketDaysTest = async (
    periodStartDate,
    periodEndDate,
    holidaySchemaId
  ) => {
    try {
      setLoading(true)
      if (isMexicanSchema(holidaySchemaId)) {
        const today = DateTime.now()
        const response = await getTeamMemberBucketDaysResume(
          periodStartDate.startOf("month"),
          periodEndDate.endOf("month"),
          today.startOf("month").set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          }),
          today.endOf("month").set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          })
        )
        setBucketDaysList(response.result)
      } else {
        setBucketDaysList(null)
      }
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const updateTeamMemberBucketDaysExcludeVacationId = async (
    periodStartDate,
    periodEndDate,
    holidaySchemaId,
    vacationRangeId,
    vacationStartDate
  ) => {
    try {
      setLoading(true)
      if (isMexicanSchema(holidaySchemaId)) {
        const today = DateTime.now()
        const response = await getTeamMemberBucketDaysResumeExcludeVacationID(
          periodStartDate.startOf("month"),
          periodEndDate.plus({ months: 1 }).endOf("month"),
          today.startOf("month").set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          }),
          today.endOf("month").set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          }),
          vacationRangeId,
          vacationStartDate
        )
        setBucketDaysList(response.result)
      } else {
        setBucketDaysList(null)
      }
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const updateTeamMemberPlusDays = async (
    periodStartDate,
    periodEndDate,
    holidaySchemaId
  ) => {
    try {
      setLoading(true)
      if (isMexicanSchema(holidaySchemaId)) {
        const today = DateTime.now()
        const response = await getTeamMemberPlusDaysResume(
          getTeamMembersStartOfPeriodMonth(
            user?.workAnniversary,
            periodStartDate
          ),
          getTeamMembersEndOfPeriodMonth(user?.workAnniversary, periodEndDate),
          today.startOf("month").set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          }),
          today.endOf("month").set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          })
        )
        setPlusDaysList(response.result)
      } else {
        setPlusDaysList(null)
      }
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const updateTeamMemberPlusDaysExcludeVacationId = async (
    periodStartDate,
    periodEndDate,
    holidaySchemaId,
    vacationRangeId,
    vacationStartDate
  ) => {
    try {
      setLoading(true)
      if (isMexicanSchema(holidaySchemaId)) {
        const today = DateTime.now()
        const response = await getTeamMemberPlusDaysResumeExcludeVacationID(
          periodStartDate.startOf("month"),
          periodEndDate.endOf("month"),
          today.startOf("month").set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          }),
          today.endOf("month").set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          }),
          vacationRangeId,
          vacationStartDate
        )
        setPlusDaysList(response.result)
      } else {
        setPlusDaysList(null)
      }
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const updateAvailableBucketDaysBasedOnPeriodChange = (
    startDate,
    endDate,
    currentBucketDaysList
  ) => {
    const today = DateTime.now().set({
      hour: 12,
      millisecond: 0,
      minute: 0,
      second: 0,
    })

    if (!currentBucketDaysList || isObjectEmpty(currentBucketDaysList)) return
    let newLimitIndex = null
    let dateToUse = null
    if (endDate) dateToUse = endDate
    else dateToUse = startDate

    // Date is a past request
    if (today > startDate && today > endDate) {
      setAvailableDaysList((prev) => ({
        ...prev,
        bucketDays: currentBucketDaysList.actualBucketDays,
      }))
    }
    // Date is a current request or a future request
    else {
      newLimitIndex = currentBucketDaysList.data[0].monthsData.findIndex(
        (monthData) => {
          return (
            monthData.month === dateToUse.month &&
            monthData.year === dateToUse.year
          )
        }
      )

      setAvailableDaysList((prev) => ({
        ...prev,
        bucketDays: currentBucketDaysList.actualAvailableDays[newLimitIndex],
      }))
    }
  }

  const updateAvailablePlusDaysBasedOnPeriodChange = (
    startDate,
    endDate,
    currentPlusDaysList,
    setState = true
  ) => {
    const today = DateTime.now().set({
      hour: 12,
      millisecond: 0,
      minute: 0,
      second: 0,
    })

    if (!currentPlusDaysList || isObjectEmpty(currentPlusDaysList)) return
    let newLimitIndex = null
    let dateToUse = null
    if (endDate) dateToUse = endDate
    else dateToUse = startDate
    // Date is a past request
    if (today > startDate && today > endDate) {
      const plusDays = 0
      setState &&
        setAvailableDaysList((prev) => ({
          ...prev,
          plusDays,
        }))
      return plusDays
    }
    // Date is a current request or a future request
    const teamMemberAntiquityInYears = getAntiquityFromDate({
      fromDate: getValidWorkAnniversaryForDayOffValidity(user?.workAnniversary),
      toDate: dateToUse,
    })?.years
    newLimitIndex = currentPlusDaysList.findIndex((period) => {
      return period.year === teamMemberAntiquityInYears
    })
    if (
      typesOfDaysToUse === 1 &&
      currentPlusDaysList[newLimitIndex]?.renewableDays -
        currentPlusDaysList[newLimitIndex]?.usedDays >
        0
    ) {
      setState && setTypesOfDaysToUse((prev) => prev + 1)
    }
    const plusDays =
      currentPlusDaysList[newLimitIndex]?.renewableDays -
      currentPlusDaysList[newLimitIndex]?.usedDays
    setState &&
      setAvailableDaysList((prev) => ({
        ...prev,
        plusDays,
      }))
    return plusDays
  }

  const updateAvailableAccordedDaysBasedOnPeriodChange = async (
    startDate,
    endDate,
    currentAccordedDaysList,
    setState = true
  ) => {
    const today = DateTime.now().set({
      hour: 12,
      minute: 0,
      second: 0,
      millisecond: 0,
    })
    const dateToUse = startDate
    let accordedDays = 0

    const currentYearAnniversary = DateTime.fromISO(user?.workAnniversary).set({
      year: today.year,
    })
    const nextYearAnniversary = currentYearAnniversary.plus({ years: 1 })
    const periodEnd = nextYearAnniversary.plus({ days: 1 })

    let periodStartToQuery = currentYearAnniversary
    let periodEndToQuery = nextYearAnniversary

    if (dateToUse > periodEnd) {
      periodStartToQuery = currentYearAnniversary.plus({ years: 1 })
      periodEndToQuery = nextYearAnniversary.plus({ years: 1 })
    }

    try {
      const response = await getTeamMemberVacationResume(
        today
          .startOf("month")
          .set({ hour: 12, millisecond: 0, minute: 0, second: 0 }),
        today
          .endOf("month")
          .set({ hour: 12, millisecond: 0, minute: 0, second: 0 }),
        getTeamMembersStartOfPeriodMonth(
          user?.workAnniversary,
          periodStartToQuery
        ),
        getTeamMembersEndOfPeriodMonth(user?.workAnniversary, periodEndToQuery)
      )

      if (!response?.success)
        throw new Error(response?.message || "Respuesta inválida del backend")

      accordedDays = response?.result?.availableAccordedDays || 0

      if (setState) {
        setAvailableDaysList((prev) => ({
          ...prev,
          accordedDays,
        }))
      }

      return accordedDays
    } catch (error) {
      console.error("Error al consultar días acordados:", error)
      return 0
    }
  }

  const updateDisabledDayList = async (periodStartDate, periodEndDate) => {
    try {
      setLoading(true)
      const response = await getTeamMemberDisabledDaysInRange(
        periodStartDate,
        periodEndDate
      )
      if (!response.success) {
        throw new Error(response.message)
      }

      const correctedDisabledDays = response.result.map((day) => {
        return { date: day.date.split("T")[0] }
      })

      setDisabledDayList(correctedDisabledDays)
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const updateDisabledDayListExcludeVacationId = async (
    periodStartDate,
    periodEndDate,
    vacationId
  ) => {
    try {
      setLoading(true)
      const response = await getTeamMemberDisabledDaysInRangeExcludeVacationID(
        periodStartDate,
        periodEndDate,
        vacationId
      )
      if (!response.success) {
        throw new Error(response.message)
      }

      const correctedDisabledDays = response.result.map((day) => {
        return { date: day.date.split("T")[0] }
      })

      setDisabledDayList(correctedDisabledDays)
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const updateAvailableDaysList = async (
    periodStartDate,
    periodEndDate,
    holidaySchemaId
  ) => {
    try {
      setLoading(true)
      const today = DateTime.now()
      const response = await getTeamMemberVacationResume(
        today.startOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        today.endOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        getTeamMembersStartOfPeriodMonth(
          user?.workAnniversary,
          periodStartDate
        ),
        getTeamMembersEndOfPeriodMonth(user?.workAnniversary, periodEndDate)
      )
      const bucketDaysResponse = await getTeamMemberBucketDaysAsOfToday(
        today.startOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        today.endOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        })
      )

      if (!response.success) {
        throw new Error(response.message)
      }
      if (!bucketDaysResponse.success) {
        throw new Error(bucketDaysResponse.message)
      }
      const typesOfDaysToUse = getNumberOfTypeDaysToUse({
        holidaySchemaId,
        teamMemberVacationResume: response.result,
        availableBucketDays: bucketDaysResponse.result,
      })
      setTypesOfDaysToUse(typesOfDaysToUse)
      setAvailableDaysList({
        bucketDays:
          isMexicanSchema(holidaySchemaId) ? bucketDaysResponse.result : null,
        plusDays: response.result.availablePlusDays,
        accordedDays: response.result.availableAccordedDays,
        additionalDays: response.result.availableAdditionalDays,
      })
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const updateAvailableDaysListExcludeVacationId = async (
    periodStartDate,
    periodEndDate,
    holidaySchemaId,
    vacationId
  ) => {
    try {
      setLoading(true)
      const today = DateTime.now()
      const response = await getTeamMemberVacationResumeExcludeVacationID(
        today.startOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        today.endOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        getTeamMembersStartOfPeriodMonth(
          user?.workAnniversary,
          periodStartDate
        ),
        getTeamMembersEndOfPeriodMonth(user?.workAnniversary, periodEndDate),
        vacationId
      )
      const bucketDaysResponse = await getTeamMemberBucketDaysAsOfToday(
        today.startOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        today.endOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        })
      )

      if (!response.success) {
        throw new Error(response.message)
      }
      if (!bucketDaysResponse.success) {
        throw new Error(bucketDaysResponse.message)
      }
      const availablePlusDays = updateAvailablePlusDaysBasedOnPeriodChange(
        formData.requestDates[0],
        formData.requestDates[1],
        plusDaysList,
        false
      )
      const resume = {
        ...response.result,
        availablePlusDays,
      }
      const typesOfDaysToUse = getNumberOfTypeDaysToUse({
        holidaySchemaId,
        teamMemberVacationResume: resume,
        availableBucketDays: bucketDaysResponse.result,
      })
      setTypesOfDaysToUse(typesOfDaysToUse)
      setAvailableDaysList({
        bucketDays:
          isMexicanSchema(holidaySchemaId) ? bucketDaysResponse.result : null,
        plusDays: resume.availablePlusDays,
        accordedDays: resume.availableAccordedDays,
        additionalDays: resume.availableAdditionalDays,
      })
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  return {
    // States
    formData,
    setFormData,
    validations,
    setValidations,
    availableDaysList,
    setAvailableDaysList,
    disabledDayList,
    setDisabledDayList,
    bucketDaysList,
    setBucketDaysList,
    plusDaysList,
    setPlusDaysList,
    accordedDaysList,
    setAccordedDaysList,
    multipleDays,
    setMultipleDays,
    daysToUse,
    setDaysToUse,
    typesOfDaysToUse,
    setTypesOfDaysToUse,
    disabledSaveButton,
    setDisabledSaveButton,
    allowFormStateChanges,
    setAllowFormStateChanges,
    loading,
    setLoading,
    anniversaryDate,

    // Functions
    updateDaysToUse,
    updateFormWithSingleTypeOfDayOff,
    resetCounters,
    resetForms,

    // Services
    updateTeamMemberBucketDaysTest,
    updateTeamMemberBucketDaysExcludeVacationId,
    updateTeamMemberPlusDays,
    updateTeamMemberPlusDaysExcludeVacationId,
    updateAvailableBucketDaysBasedOnPeriodChange,
    updateAvailablePlusDaysBasedOnPeriodChange,
    updateAvailableAccordedDaysBasedOnPeriodChange,
    updateDisabledDayList,
    updateDisabledDayListExcludeVacationId,
    updateAvailableDaysList,
    updateAvailableDaysListExcludeVacationId,
  }
}

export default useMyVacationRequestFormState
